/**
 * Pure, model-derived lookups that used to live as inline `useMemo`s inside the
 * App god component. Pulled out so they're unit-testable in isolation: each is a
 * plain function of (model / indexes / global layout context), no React, no refs.
 *
 * - `buildBoardIndexes` — per-module / per-interface connection participation,
 *   keyed by the board edge/port ids the InfiniteBoard uses for selection.
 * - `buildEdgeSelectLookup` — id → BoardEdge for resolving a selected edge.
 * - `buildDiagnosticsByModule` — best-effort diagnostic → owning-module mapping.
 * - `resolveSelectedAspect` — an `aspect:<key>=<value>` synthetic node → detail.
 * - `resolveSelectedInterface` — an interface key → its inspector payload.
 * - `resolveArchlangNav` — an `archlang://` link → a selection action.
 */

import type {
  ArchitectureModel,
  Diagnostic,
  Interface,
  Module,
  StableId,
} from "@archlang/scene/arch-model";
import type { BoardEdge, CallDetail, LayoutContext } from "@archlang/scene/board";
import { ancestryTest, portAsEdge, portHalves } from "./board/ports.js";
import { aspectValueOf, aspectColorFor } from "./aspectColors";
import { processKey, processQualifiedName } from "./processKey.js";
import { typeSelectionKey } from "./typeKey.js";
import { parseArchlangUri } from "./description/render";
import { buildQualifiedName, type ModelIndexes } from "./hooks/useModelIndexes";
import type { AspectDetail, BoardIndexes, ConnRef } from "./components/Sidebar";

/**
 * Per-module / per-interface connection participation, built from
 * `prepareLayout`'s GLOBAL edge/port aggregation — the same BoardEdges /
 * external-ports / internal-calls the solver assigns to every space, but
 * computed for the whole model without solving any space. This decouples the
 * connection index from the (lazily-solved) board tree: sidebar link counts +
 * connection details stay correct for every module, even ones whose space
 * hasn't been laid out yet.
 */
export function buildBoardIndexes(
  globalCtx: LayoutContext | null,
  indexes: ModelIndexes | null,
): BoardIndexes {
  const connByModule = new Map<string, ConnRef[]>();
  const processesByModule = new Map<string, Set<string>>();
  const connByInterface = new Map<string, ConnRef[]>();
  const push = (m: Map<string, ConnRef[]>, k: string, ref: ConnRef) => {
    const arr = m.get(k) ?? [];
    arr.push(ref);
    m.set(k, arr);
  };
  const noteIface = (ifaceId: string | undefined, ref: ConnRef) => {
    if (!ifaceId) return;
    push(connByInterface, ifaceId, ref);
  };
  const noteProcesses = (moduleId: string, details: readonly { processId?: string }[]) => {
    let set = processesByModule.get(moduleId);
    for (const d of details) {
      if (!d.processId) continue;
      if (!set) {
        set = new Set();
        processesByModule.set(moduleId, set);
      }
      set.add(d.processId);
    }
  };
  const labelOf = (id: string) => indexes?.moduleById.get(id)?.name ?? id;
  const under = ancestryTest(indexes?.moduleParentById);
  if (globalCtx) {
    const ctx = globalCtx;
    for (const edgeList of ctx.edgesByParent.values()) {
      for (const e of edgeList) {
        const fromRef: ConnRef = { edgeId: e.id, peerId: e.to, peerLabel: labelOf(e.to), direction: "out", details: e.details };
        const toRef: ConnRef = { edgeId: e.id, peerId: e.from, peerLabel: labelOf(e.from), direction: "in", details: e.details };
        push(connByModule, e.from, fromRef);
        push(connByModule, e.to, toRef);
        noteProcesses(e.from, e.details);
        noteProcesses(e.to, e.details);
        for (const d of e.details) {
          noteIface(d.fromInterfaceId, fromRef);
          noteIface(d.toInterfaceId, toRef);
        }
      }
    }
    for (const [moduleId, ports] of ctx.externalPorts) {
      for (const port of ports) {
        // One ⇄ pill on the board, but "calls out" and "called by" are separate
        // lists here — see portHalves.
        for (const [direction, details] of portHalves(port, under)) {
          const ref: ConnRef = { edgeId: port.id, peerId: port.peerId, peerLabel: labelOf(port.peerId), direction, details };
          push(connByModule, moduleId, ref);
          noteProcesses(moduleId, details);
          for (const d of details) {
            noteIface(direction === "out" ? d.fromInterfaceId : d.toInterfaceId, ref);
          }
        }
      }
    }
    for (const [moduleId, calls] of ctx.internalCalls) {
      if (!calls.length) continue;
      const ref: ConnRef = { edgeId: moduleId, peerId: moduleId, peerLabel: labelOf(moduleId), direction: "internal", details: calls };
      push(connByModule, moduleId, ref);
      noteProcesses(moduleId, calls);
      for (const d of calls) {
        noteIface(d.fromInterfaceId, ref);
        noteIface(d.toInterfaceId, ref);
      }
    }
  }
  return { connByModule, processesByModule, connByInterface };
}

/**
 * id → BoardEdge for any sibling edge or external port (synthesized into
 * BoardEdge shape) anywhere in the model. Built from the global aggregation, so
 * a selected edge resolves without a solved tree.
 */
export function buildEdgeSelectLookup(globalCtx: LayoutContext | null): Map<string, BoardEdge> {
  const m = new Map<string, BoardEdge>();
  if (!globalCtx) return m;
  for (const edgeList of globalCtx.edgesByParent.values()) {
    for (const e of edgeList) m.set(e.id, e);
  }
  for (const [moduleId, ports] of globalCtx.externalPorts) {
    for (const port of ports) m.set(port.id, portAsEdge(port, moduleId));
  }
  return m;
}

/**
 * Associate each diagnostic with an owning module id (best-effort: parse the
 * first quoted qualified name from the message, try it as an interface, then as
 * a module from longest to shortest dotted prefix). Unmatched diagnostics drop.
 */
export function buildDiagnosticsByModule(
  diagnostics: readonly Diagnostic[],
  indexes: ModelIndexes | null,
): Map<StableId, Diagnostic[]> {
  const m = new Map<StableId, Diagnostic[]>();
  if (!indexes) return m;
  const interfaceRe = /'([^']+)'/;
  for (const d of diagnostics) {
    const match = d.message.match(interfaceRe);
    if (!match) continue;
    const qn = match[1]!;
    let moduleId: StableId | undefined;
    const ifaceHit = indexes.interfaceByQualifiedName.get(qn);
    if (ifaceHit) moduleId = ifaceHit.moduleId;
    if (!moduleId) {
      const parts = qn.split(".");
      for (let i = parts.length; i >= 1; i--) {
        const name = parts.slice(0, i).join(".");
        for (const [id, mod] of indexes.moduleById) {
          if (mod.name === name || buildQualifiedName(mod, indexes.moduleById) === name) {
            moduleId = id as StableId;
            break;
          }
        }
        if (moduleId) break;
      }
    }
    if (!moduleId) continue;
    const list = m.get(moduleId) ?? [];
    // Dedup per node: a module referenced from N process steps yields N
    // identical diagnostics (e.g. "module 'Foo' is not defined yet"). The board
    // shows ONE node per module, so collapse identical (severity+message) labels
    // — the editor still flags every reference site; the board badge shouldn't.
    if (!list.some((e) => e.severity === d.severity && e.message === d.message)) {
      list.push(d);
    }
    m.set(moduleId, list);
  }
  return m;
}

/**
 * Resolve an `aspect:<key>=<json-value>` synthetic node id to its inspector
 * detail (key, value, colour, member modules). Returns null for any non-aspect
 * id or malformed payload.
 */
export function resolveSelectedAspect(
  selectedModuleId: string | null,
  model: ArchitectureModel | null,
): AspectDetail | null {
  if (!selectedModuleId || !selectedModuleId.startsWith("aspect:") || !model) return null;
  const rest = selectedModuleId.slice("aspect:".length);
  const eq = rest.indexOf("=");
  if (eq < 0) return null;
  const key = rest.slice(0, eq);
  let value: string;
  try {
    value = JSON.parse(rest.slice(eq + 1)) as string;
  } catch {
    return null;
  }
  const members: { id: string; name: string; qualifiedName: string }[] = [];
  const walk = (mods: readonly Module[], parent: string): void => {
    for (const m of mods) {
      const q = parent ? `${parent}.${m.name}` : m.name;
      if (aspectValueOf(m, key) === value) members.push({ id: m.id ?? q, name: m.name, qualifiedName: q });
      walk(m.children, q);
    }
  };
  walk(model.modules, "");
  return { key, value, color: aspectColorFor(value), members };
}

export interface SelectedInterface {
  readonly iface: Interface;
  readonly moduleId: StableId;
  readonly qualifiedName: string;
}

/** Resolve an interface key (`Module.Path.Interface`) to its inspector payload. */
export function resolveSelectedInterface(
  selectedInterfaceKey: string | null,
  indexes: ModelIndexes | null,
): SelectedInterface | null {
  if (!selectedInterfaceKey || !indexes) return null;
  const entry = indexes.interfaceById.get(selectedInterfaceKey);
  if (!entry) return null;
  return {
    iface: entry.iface,
    moduleId: entry.moduleId,
    qualifiedName: selectedInterfaceKey,
  };
}

/**
 * A resolved navigation target for an `archlang://` link. The renderer emits:
 *   - `archlang://id/<stableId>`        → module / process / interface
 *   - `archlang://name/<qualifiedName>` → module / process / interface
 *   - `archlang://doc/<name>#<slug>`    → a markdown document (+ section)
 *   - `archlang://aspect/<key>[:<v>]`   → the aspect overlay / selection
 * View targets aren't selectable from the board, so they resolve to null.
 *
 * `document` and `aspect` targets don't need the resolved model/indexes — they
 * carry everything the handler needs — so they resolve even before a model
 * loads. Element targets (id / name) still require the indexes to dereference.
 */
export type ArchlangNavAction =
  | { readonly kind: "process"; readonly processId: StableId }
  | { readonly kind: "module"; readonly moduleKey: StableId }
  | { readonly kind: "interface"; readonly interfaceId: StableId }
  | { readonly kind: "document"; readonly name: string; readonly slug?: string }
  | { readonly kind: "aspect"; readonly key: string; readonly value?: string }
  | { readonly kind: "type"; readonly name: string };

/** Route an `archlang://` link to a selection action (module / process /
 *  interface / document / aspect), or null when it resolves to nothing
 *  actionable. */
export function resolveArchlangNav(
  href: string,
  model: ArchitectureModel | null,
  indexes: ModelIndexes | null,
): ArchlangNavAction | null {
  const ref = parseArchlangUri(href);
  if (!ref) return null;
  // Document + aspect targets are self-describing — route them without needing
  // the resolved model (the surfaces look up / fall back gracefully).
  if (ref.kind === "doc") return { kind: "document", name: ref.doc, slug: ref.slug };
  if (ref.kind === "aspect") return { kind: "aspect", key: ref.key, value: ref.value };
  if (!model || !indexes) return null;
  if (ref.kind === "id") {
    const proc = indexes.processById.get(ref.value);
    if (proc) return { kind: "process", processId: processKey(proc) as StableId };
    for (const [mKey, mod] of indexes.moduleById) {
      if (mod.id === ref.value || mKey === ref.value) {
        return { kind: "module", moduleKey: mKey as StableId };
      }
    }
    return null;
  }
  // `name` form: qualified name. Modules first, then interfaces, then processes.
  for (const [mKey, qname] of indexes.moduleQualifiedNameById) {
    if (qname === ref.value) return { kind: "module", moduleKey: mKey as StableId };
  }
  const iface = indexes.interfaceByQualifiedName.get(ref.value);
  if (iface) return { kind: "interface", interfaceId: iface.interfaceId as StableId };
  // A surface path (`Gateway.Http`) lands on its owning module — surfaces
  // aren't separately selectable (the search index's law); without this arm
  // a surface-source backlink row was a silent dead pick (r52).
  const surfaceOwner = indexes.surfaceOwnerByPath.get(ref.value);
  if (surfaceOwner) return { kind: "module", moduleKey: surfaceOwner };
  const proc =
    model.processes.find((p) => processKey(p) === ref.value) ??
    model.processes.find((p) => p.name === ref.value);
  if (proc) return { kind: "process", processId: processKey(proc) as StableId };
  // An owned-subprocess address (`Billing.BookSlot`) lands where ⌘F does —
  // process-owned on its process, module-owned on its owner module.
  // Free-standing subprocesses have no board home (search skips them too),
  // so they stay null (r53).
  for (const sp of model.subprocesses ?? []) {
    const owner = sp.ownerPath && sp.ownerPath.length > 0 ? sp.ownerPath.join(".") : "";
    if (!owner || `${owner}.${sp.name}` !== ref.value) continue;
    const ownerProc = model.processes.find(
      (p) => processKey(p) === owner || processQualifiedName(p) === owner,
    );
    if (ownerProc) return { kind: "process", processId: processKey(ownerProc) as StableId };
    for (const [mKey, qname] of indexes.moduleQualifiedNameById) {
      if (qname === owner) return { kind: "module", moduleKey: mKey as StableId };
    }
  }
  // A declared type's link opens the types surface — the same landing the
  // search palette and the nav tree give it (`store.openType`) (r53). Types
  // are space-scoped, so the action carries the qualified selection key; a
  // bare ref lands on its first match (r56).
  const ty = model.types?.find(
    (t) => t.name === ref.value || typeSelectionKey(t) === ref.value,
  );
  if (ty) return { kind: "type", name: typeSelectionKey(ty) };
  return null;
}
